import { useState, useEffect } from 'react';
import { getOperators } from '../utils/storage';
import { supabase } from '../utils/supabase';
import { CATEGORIES, TIERS, scoreUnit, getTier } from '../utils/scoring';

export default function Reports() {
  const [operators, setOperators] = useState([]);
  const [activations, setActivations] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('portfolio');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    const [ops, actRes, compRes] = await Promise.all([
      getOperators(),
      supabase.from('bc_activations').select('*').order('created_at', { ascending: false }),
      supabase.from('bc_compliance').select('*').order('updated_at', { ascending: false }),
    ]);
    setOperators(ops);
    setActivations(actRes.data || []);
    setCompliance(compRes.data || []);
    setLoading(false);
  };

  const allUnits = operators.flatMap(o => (o.data?.units || []).map(u => ({ ...u, operatorName: o.data?.operatorName || o.operator_name, opId: o.id })));
  const scored = allUnits.filter(u => +u.totalSales > 0).map(u => ({ ...u, results: scoreUnit(u) })).filter(u => u.results);

  // Tier distribution
  const tierDist = {};
  TIERS.forEach(t => { tierDist[t.name] = 0; });
  scored.forEach(u => u.results.forEach(r => { tierDist[r.tier.name]++; }));

  // Category averages
  const catStats = {};
  CATEGORIES.forEach(c => { catStats[c.id] = { total: 0, count: 0 }; });
  scored.forEach(u => u.results.forEach(r => { catStats[r.id].total += r.finalScore; catStats[r.id].count++; }));

  // Activation stats
  const actByStatus = { planned: 0, active: 0, completed: 0, cancelled: 0 };
  activations.forEach(a => { actByStatus[a.data?.status || 'planned']++; });

  // Compliance stats
  const compByStatus = { submitted: 0, approved: 0, rejected: 0 };
  compliance.forEach(c => { compByStatus[c.data?.status || 'submitted']++; });

  if (loading) return <div className="py-20 text-center text-neutral-500">Loading...</div>;

  return (
    <div className="py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-white">Reports</h1>
          <p className="text-xs text-neutral-500">Quarterly reporting and analytics</p>
        </div>
        <button onClick={() => window.print()} className="btn-primary text-xs py-2">Export PDF</button>
      </div>

      {/* Report type tabs */}
      <div className="flex gap-1 mb-6">
        {['portfolio', 'activations', 'compliance', 'supplier'].map(t => (
          <button key={t} onClick={() => setReportType(t)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${reportType === t ? 'bg-brand-gold/15 text-brand-gold' : 'text-neutral-500 hover:text-neutral-300'}`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Portfolio Report */}
      {reportType === 'portfolio' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            {TIERS.map(tier => (
              <div key={tier.name} className="card p-4 text-center" style={{ borderTop: `3px solid ${tier.color}` }}>
                <p className="text-2xl font-black" style={{ color: tier.color }}>{tierDist[tier.name]}</p>
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider">{tier.name}</p>
              </div>
            ))}
          </div>

          <div className="card p-5 mb-6">
            <h2 className="text-sm font-semibold text-brand-gold mb-4">Category Performance</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {CATEGORIES.map(cat => {
                const avg = catStats[cat.id].count > 0 ? Math.round(catStats[cat.id].total / catStats[cat.id].count) : 0;
                const tier = getTier(avg);
                return (
                  <div key={cat.id} className="text-center">
                    <p className="text-2xl">{cat.icon}</p>
                    <p className="text-xs text-neutral-400 mt-1">{cat.name}</p>
                    <p className="text-2xl font-black mt-1" style={{ color: tier.color }}>{avg}</p>
                    <p className="text-[9px]" style={{ color: tier.color }}>{tier.name}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card p-5">
            <h2 className="text-sm font-semibold text-brand-gold mb-4">Portfolio Summary</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center"><p className="text-3xl font-black text-white">{operators.length}</p><p className="text-[10px] text-neutral-500">Operators</p></div>
              <div className="text-center"><p className="text-3xl font-black text-white">{scored.length}</p><p className="text-[10px] text-neutral-500">Scored Units</p></div>
              <div className="text-center"><p className="text-3xl font-black text-brand-gold">{activations.length}</p><p className="text-[10px] text-neutral-500">Activations</p></div>
              <div className="text-center"><p className="text-3xl font-black text-emerald-400">{compByStatus.approved}</p><p className="text-[10px] text-neutral-500">Compliant</p></div>
            </div>
          </div>
        </>
      )}

      {/* Activations Report */}
      {reportType === 'activations' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {Object.entries(actByStatus).map(([status, count]) => (
              <div key={status} className="card p-4 text-center">
                <p className="text-2xl font-black text-white">{count}</p>
                <p className={`text-[10px] uppercase tracking-wider ${status === 'completed' ? 'text-emerald-400' : status === 'active' ? 'text-blue-400' : status === 'cancelled' ? 'text-red-400' : 'text-amber-400'}`}>{status}</p>
              </div>
            ))}
          </div>
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-brand-gold mb-4">All Activations</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-neutral-800"><th className="text-left py-2">Name</th><th className="text-left py-2">Category</th><th className="text-left py-2">Date</th><th className="text-left py-2">Supplier</th><th className="text-center py-2">Status</th></tr></thead>
                <tbody>{activations.map(a => {
                  const cat = CATEGORIES.find(c => c.id === a.data?.category);
                  return (
                    <tr key={a.id} className="border-b border-neutral-800/30">
                      <td className="py-2 font-medium text-white">{a.data?.name}</td>
                      <td className="py-2">{cat?.icon} {cat?.name}</td>
                      <td className="py-2 text-neutral-500">{a.data?.date || '—'}</td>
                      <td className="py-2 text-neutral-500">{a.data?.supplier || '—'}</td>
                      <td className="py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${a.data?.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' : a.data?.status === 'active' ? 'bg-blue-500/15 text-blue-400' : 'bg-amber-500/15 text-amber-400'}`}>{a.data?.status || 'planned'}</span></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Compliance Report */}
      {reportType === 'compliance' && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {Object.entries(compByStatus).map(([status, count]) => (
              <div key={status} className="card p-4 text-center">
                <p className="text-2xl font-black text-white">{count}</p>
                <p className={`text-[10px] uppercase tracking-wider ${status === 'approved' ? 'text-emerald-400' : status === 'rejected' ? 'text-red-400' : 'text-amber-400'}`}>{status}</p>
              </div>
            ))}
          </div>
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-brand-gold mb-4">Compliance Records</h2>
            <div className="space-y-2">
              {compliance.map(c => (
                <div key={c.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-white">{c.unit_name} — {c.data?.type}</p>
                    {c.data?.notes && <p className="text-[10px] text-neutral-500">{c.data.notes}</p>}
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded font-medium ${c.data?.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : c.data?.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                    {c.data?.status || 'submitted'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Supplier Report */}
      {reportType === 'supplier' && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Supplier-Facing Report</h2>
          <p className="text-xs text-neutral-500 mb-4">Portfolio summary for supplier partners — no revenue data shown.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-neutral-800">
                <th className="text-left py-2">Operator</th><th className="text-left py-2">Unit</th><th className="text-left py-2">Market</th><th className="text-left py-2">Venue</th>
                {CATEGORIES.map(c => <th key={c.id} className="text-center py-2">{c.icon}</th>)}
                <th className="text-center py-2">Activation</th>
              </tr></thead>
              <tbody>{scored.map((u, i) => (
                <tr key={i} className="border-b border-neutral-800/30">
                  <td className="py-2 font-medium text-white">{u.operatorName}</td>
                  <td className="py-2 text-neutral-400">{u.name}</td>
                  <td className="py-2 text-neutral-500">{u.market}</td>
                  <td className="py-2 text-neutral-600">{u.type}</td>
                  {u.results.map(r => <td key={r.id} className="text-center py-2"><span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${r.tier.cls}`}>T{r.tier.num}</span></td>)}
                  <td className="text-center py-2"><span className={`text-[9px] px-2 py-0.5 rounded ${u.activation === 'High' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-neutral-800 text-neutral-500'}`}>{u.activation || 'Med'}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-center text-[8px] text-neutral-700 mt-8">NEG Proprietary Category Support Scoring System · Beverage Collective</p>
    </div>
  );
}
